import type { ComponentType } from "react";
import { AnimatorPreview } from "@/components/features/assets/preview/animator-preview";
import { AssetRawDataEditor } from "@/components/features/assets/editors/asset-raw-data-editor";
import { AssetJsonContentEditor } from "@/components/features/assets/editors/asset-json-content-editor";
import { SceneGraphEditor } from "@/components/features/assets/editors/scene-graph-editor";
import { ScenePreview } from "@/components/features/assets/preview/scene-preview";
import { VrmPreview } from "@/components/features/assets/preview/vrm-preview";
import { VrmaPreview } from "@/components/features/assets/preview/vrma-preview";
import { Asset3DPreview } from "@/components/features/assets";
import { MarkdownPreview } from "@/components/features/assets/preview/markdown-preview";
import { ImagePreview } from "@/components/features/assets/preview/image-preview";
import { VideoPreview } from "@/components/features/assets/preview/video-preview";
import { RivePreview } from "@/components/features/assets/preview/rive-preview";
import { TextEditor } from "@/components/features/assets/editors/text-editor";
import { CsvPreview } from "@/components/features/assets/preview/csv-preview";
import { CsvEditor } from "@/components/features/assets/editors/csv-editor";
// In the future, you would import other editors here:
// import { ScenePreview } from "@/components/features/scene/scene-preview";
// import { SceneGraphEditor } from "@/components/features/scene/scene-graph-editor";

// Define the shape of an editor configuration
export type EditorComponentContext = {
  assetId: string;
  assetType: string;
};

export interface EditorConfig<Props extends { assetId: string } = { assetId: string }> {
  id: string; // e.g., 'json', 'preview'
  label: string; // e.g., 'JSON Editor', 'Preview'
  component: ComponentType<Props>;
  getProps?: (context: EditorComponentContext) => Omit<Props, "assetId">;
  // Future: Add a validator function for this asset type
  // validator?: (data: any) => { isValid: boolean; errors?: string[] };
}

type EditorRegistry = Record<string, EditorConfig<any>[]>;

// The registry itself
// We map an asset type (string) to an array of available editors
export const editorRegistry: EditorRegistry = {
  // The default editor configuration for any asset type not explicitly defined
  'default': [
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'application/json': [
    { id: 'json-content', label: 'Content', component: AssetJsonContentEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'application/scene+json': [
    { id: 'json-content', label: 'JSON Editor', component: AssetJsonContentEditor },
    { id: 'preview', label: 'Preview', component: ScenePreview },
    { id: 'graph', label: 'Graph Editor', component: SceneGraphEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'model/vrm+gltf' : [
    { id: 'preview', label: 'Preview', component: VrmPreview },
    { id: 'graph', label: 'Graph Editor', component: SceneGraphEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'animation/vrma+gltf' : [
    { id: 'preview', label: 'Preview', component: VrmaPreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'application/animator-controller+json' : [
    { id: 'json-content', label: 'JSON Editor', component: AssetJsonContentEditor },
    { id: 'preview', label: 'Preview', component: AnimatorPreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'application/agent.persona+json': [
    { id: 'json-content', label: 'JSON Editor', component: AssetJsonContentEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'application/agent.voice+json': [
    { id: 'json-content', label: 'JSON Editor', component: AssetJsonContentEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'application/game-settings+json': [
    { id: 'json-content', label: 'JSON Editor', component: AssetJsonContentEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'application/item-database+json': [
    { id: 'json-content', label: 'JSON Editor', component: AssetJsonContentEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'model/gltf+json' : [
    { 
      id: 'preview', 
      label: 'Preview', 
      component: Asset3DPreview,
      getProps: ({ assetType }) => ({ fileType: assetType })
    },
    { id: 'graph', label: 'Graph Editor', component: SceneGraphEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'model/fbx' : [
    { 
      id: 'preview', 
      label: 'Preview', 
      component: Asset3DPreview,
      getProps: ({ assetType }) => ({ fileType: assetType })
    },
    { id: 'graph', label: 'Graph Editor', component: SceneGraphEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],

  'model/vnd.collada+xml' : [
    { 
      id: 'preview', 
      label: 'Preview', 
      component: Asset3DPreview,
      getProps: ({ assetType }) => ({ fileType: assetType })
    },
    { id: 'graph', label: 'Graph Editor', component: SceneGraphEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'text/markdown' : [
    { id: 'preview', label: 'Preview', component: MarkdownPreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'text/plain' : [
    { id: 'editor', label: 'Text Editor', component: TextEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'image/jpeg' : [
    { id: 'preview', label: 'Preview', component: ImagePreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'image/png' : [
    { id: 'preview', label: 'Preview', component: ImagePreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'image/gif' : [
    { id: 'preview', label: 'Preview', component: ImagePreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'image/webp' : [
    { id: 'preview', label: 'Preview', component: ImagePreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'image/svg+xml' : [
    { id: 'preview', label: 'Preview', component: ImagePreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'video/mp4' : [
    { id: 'preview', label: 'Preview', component: VideoPreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'video/webm' : [
    { id: 'preview', label: 'Preview', component: VideoPreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'video/quicktime' : [
    { id: 'preview', label: 'Preview', component: VideoPreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'application/rive' : [
    { id: 'preview', label: 'Preview', component: RivePreview },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ],
  
  'text/csv' : [
    { id: 'preview', label: 'Preview', component: CsvPreview },
    { id: 'editor', label: 'CSV Editor', component: CsvEditor },
    { id: 'raw-data', label: 'Raw Data', component: AssetRawDataEditor },
  ]
};

const DEFAULT_ASSET_TYPE = 'default';

export const getEditorsForAssetType = (assetType: string) => {
  return editorRegistry[assetType] ?? editorRegistry[DEFAULT_ASSET_TYPE];
};

export const findEditorConfig = (assetType: string, editorId: string) => {
  return getEditorsForAssetType(assetType).find((config) => config.id === editorId);
};

export const getPreviewEditorConfig = (assetType: string) => {
  return findEditorConfig(assetType, 'preview');
};
